import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const DataQualityPanel = ({ liveData, devices }) => {
  const calculateDataQuality = () => {
    const totalDevices = devices?.length;
    const devicesWithData = Object.keys(liveData || {})?.length;
    return totalDevices > 0 ? ((devicesWithData / totalDevices) * 100)?.toFixed(0) : 0;
  };

  const detectOutliers = () => {
    // Mock outlier detection
    return Math.floor(Math.random() * 3);
  };

  const getMissingDataCount = () => {
    // Mock missing data count
    return Math.floor(Math.random() * 5);
  };

  const getDuplicateCount = () => {
    // Mock duplicate count
    return Math.floor(Math.random() * 2);
  };

  const dataQuality = calculateDataQuality();
  const outliers = detectOutliers();
  const missingData = getMissingDataCount();
  const duplicates = getDuplicateCount();

  const qualityMetrics = [
    {
      label: 'Data Completeness',
      value: `${dataQuality}%`,
      icon: 'Database',
      color: dataQuality >= 80 ? 'text-success' : dataQuality >= 50 ? 'text-warning' : 'text-error',
      bgColor: dataQuality >= 80 ? 'bg-success/10' : dataQuality >= 50 ? 'bg-warning/10' : 'bg-error/10'
    },
    {
      label: 'Outliers Detected',
      value: outliers,
      icon: 'AlertTriangle',
      color: outliers === 0 ? 'text-success' : 'text-warning',
      bgColor: outliers === 0 ? 'bg-success/10' : 'bg-warning/10'
    },
    {
      label: 'Missing Data Points',
      value: missingData,
      icon: 'XCircle',
      color: missingData === 0 ? 'text-success' : 'text-error',
      bgColor: missingData === 0 ? 'bg-success/10' : 'bg-error/10'
    },
    {
      label: 'Duplicate Entries',
      value: duplicates,
      icon: 'Copy',
      color: duplicates === 0 ? 'text-success' : 'text-warning',
      bgColor: duplicates === 0 ? 'bg-success/10' : 'bg-warning/10'
    }
  ];

  const recentIssues = [
    { type: 'warning', message: 'Heart rate spike detected on Garmin Watch', time: '2 min ago' },
    { type: 'info', message: 'Sleep data synchronized successfully', time: '5 min ago' },
    { type: 'success', message: 'All devices reporting normally', time: '10 min ago' }
  ];

  const getIssueIcon = (type) => {
    if (type === 'warning') return { icon: 'AlertTriangle', color: 'text-warning' };
    if (type === 'error') return { icon: 'XCircle', color: 'text-error' };
    if (type === 'success') return { icon: 'CheckCircle', color: 'text-success' };
    return { icon: 'Info', color: 'text-primary' };
  };

  return (
    <div className="bg-card rounded-xl p-6 elevation-2">
      <div className="flex items-center space-x-3 mb-6">
        <div className="p-2 bg-error/10 rounded-lg">
          <Icon name="Shield" size={24} className="text-error" />
        </div>
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Data Quality Control</h2>
          <p className="text-sm text-muted-foreground">Monitor data integrity and detect anomalies</p>
        </div>
      </div>
      {/* Quality Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        {qualityMetrics?.map((metric, index) => (
          <motion.div
            key={metric?.label}
            className="bg-muted/30 rounded-xl p-4 border border-wellness-light"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <div className="flex items-center justify-between mb-2">
              <div className={`p-2 rounded-lg ${metric?.bgColor}`}>
                <Icon name={metric?.icon} size={18} className={metric?.color} />
              </div>
            </div>
            <p className={`text-2xl font-bold ${metric?.color} mb-1`}>{metric?.value}</p>
            <p className="text-xs text-muted-foreground">{metric?.label}</p>
          </motion.div>
        ))}
      </div>
      {/* Recent Issues Log */}
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4">Recent Activity Log</h3>
        <div className="space-y-3">
          {recentIssues?.map((issue, index) => {
            const iconInfo = getIssueIcon(issue?.type);
            return (
              <motion.div
                key={index}
                className="flex items-start space-x-3 p-3 bg-muted/30 rounded-lg"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Icon name={iconInfo?.icon} size={18} className={`${iconInfo?.color} flex-shrink-0 mt-0.5`} />
                <div className="flex-1">
                  <p className="text-sm text-foreground">{issue?.message}</p>
                  <p className="text-xs text-muted-foreground mt-1">{issue?.time}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
      {/* Data Quality Actions */}
      <div className="mt-6 p-4 bg-primary/5 border border-primary/20 rounded-lg">
        <div className="flex items-start space-x-3">
          <Icon name="Info" size={18} className="text-primary flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-xs font-medium text-foreground mb-1">Data Quality Tips</p>
            <ul className="text-xs text-muted-foreground space-y-1">
              <li>• Outliers are automatically flagged for manual review</li>
              <li>• Missing data points are filled with interpolated values when possible</li>
              <li>• Duplicate entries are automatically deduplicated during sync</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataQualityPanel;
